import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Loader2, RefreshCw, TrendingUp, AlertTriangle, ArrowDown,
  Target, Search, Crown, Bot, Lightbulb, BadgeAlert, Share2, Copy, Check,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SUBSCORE_LABEL = {
  firm_brand_clarity: 'Firm Brand Clarity',
  practice_area_clarity: 'Practice Area Clarity',
  local_legal_discoverability: 'Local Legal Discoverability',
  ai_citation_readiness: 'AI Citation Readiness',
  conversational_search_readiness: 'Conversational Search Readiness',
  content_depth: 'Content Depth',
  competitor_visibility_strength: 'Competitor Visibility Strength',
};

function ScoreRing({ score = 0, label, size = 'lg' }) {
  const pct = Math.max(0, Math.min(100, Number(score) || 0));
  const radius = size === 'lg' ? 56 : 32;
  const stroke = size === 'lg' ? 8 : 6;
  const c = 2 * Math.PI * radius;
  const offset = c - (pct / 100) * c;
  const color = pct < 45 ? '#dc2626' : pct < 65 ? '#f59e0b' : '#10b981';
  return (
    <div className={`relative ${size === 'lg' ? 'w-32 h-32' : 'w-20 h-20'}`}>
      <svg viewBox={`0 0 ${(radius + stroke) * 2} ${(radius + stroke) * 2}`} className="w-full h-full -rotate-90">
        <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={stroke} />
        <circle
          cx={radius + stroke} cy={radius + stroke} r={radius} fill="none"
          stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className={`font-bold text-slate-900 ${size === 'lg' ? 'text-3xl' : 'text-lg'}`} style={{ fontFamily: 'Outfit' }}>{pct}</div>
        {label && size === 'lg' && <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>}
      </div>
    </div>
  );
}

function IntentBadge({ intent }) {
  const map = {
    transactional: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    commercial: 'bg-violet-100 text-violet-700 border-violet-200',
    local: 'bg-sky-100 text-sky-700 border-sky-200',
    informational: 'bg-amber-100 text-amber-700 border-amber-200',
    conversational: 'bg-pink-100 text-pink-700 border-pink-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${map[intent] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
      {intent || '-'}
    </span>
  );
}

function ScrollIndicator({ visible }) {
  if (!visible) return null;
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur border border-slate-200 shadow-lg" data-testid="snapshot-scroll-indicator">
      <span className="text-xs font-semibold text-slate-700">Scroll to explore insights</span>
      <ArrowDown className="w-4 h-4 text-slate-700 animate-bounce" />
    </div>
  );
}

export default function GrowthSnapshot({ user, selectedClient }) {
  const firmId = selectedClient?.firm_id || selectedClient?.firmId || user.firm?.id || user.firm_id;
  const isClient = user.role === 'client';
  const canEdit = user.role === 'super_admin' || user.role === 'internal_team';

  const [snapshot, setSnapshot] = useState(undefined); // undefined = loading; null = none; {} = exists
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    setSharing(true);
    setError('');
    try {
      const res = await axios.post(`${API}/growth-snapshot/${firmId}/share`, {}, { headers: authHeaders() });
      const url = res.data?.share_url || '';
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch (_e) { /* clipboard may be blocked */ }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create a share link.');
    } finally {
      setSharing(false);
    }
  };

  const copyShare = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch (_e) { /* noop */ }
  };

  const fetchSnapshot = async () => {
    if (!firmId) return;
    try {
      const res = await axios.get(`${API}/growth-snapshot/${firmId}`, { headers: authHeaders() });
      const data = res.data || {};
      setSnapshot(data.exists === false ? null : data);
    } catch (_e) { setSnapshot(null); }
  };

  useEffect(() => { fetchSnapshot(); }, [firmId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show scroll indicator briefly after generation if there's content below fold
  useEffect(() => {
    if (!snapshot || !snapshot.keyword_opportunities) return;
    setShowScrollHint(true);
    const t = setTimeout(() => setShowScrollHint(false), 6000);
    const onScroll = () => setShowScrollHint(false);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { clearTimeout(t); window.removeEventListener('scroll', onScroll); };
  }, [snapshot?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async (force = false) => {
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/growth-snapshot/${firmId}/generate`,
        { force },
        { headers: authHeaders(), timeout: 90000 }
      );
      setSnapshot(res.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not generate snapshot. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // Top 3 opportunities = highest opportunity_score
  const top3 = useMemo(() => {
    const arr = (snapshot?.keyword_opportunities || []).slice();
    arr.sort((a, b) => (b.opportunity_score || 0) - (a.opportunity_score || 0));
    return arr.slice(0, 3);
  }, [snapshot]);

  if (!firmId) {
    return <div className="text-center py-16 text-slate-500">Select a client to view their snapshot.</div>;
  }

  if (snapshot === undefined && !generating && !error) {
    // Loading the existing-or-not check
    return <div className="text-center py-16 text-slate-400" data-testid="snapshot-loading">Loading…</div>;
  }

  // ── Before-generation state ─────────────────────────────────────
  if (!snapshot || !snapshot.keyword_opportunities) {
    return (
      <div className="bg-white min-h-[80vh] -m-6 p-6 sm:p-10" data-testid="growth-snapshot-empty">
        <div className="max-w-3xl mx-auto pt-12">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-50 border border-violet-100 text-violet-700 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> Free Snapshot
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Growth Opportunity Snapshot
            </h1>
            <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-xl mx-auto">
              Click below to generate your <strong>Search and AI Engine</strong> opportunity analysis. Tailored to your firm's practice areas, locations, and competitors.
            </p>
          </div>

          <div className="mt-12 flex flex-col items-center">
            <button
              onClick={() => handleGenerate(false)}
              disabled={generating}
              title="Click to generate your free AI opportunity analysis"
              data-testid="generate-snapshot-btn"
              className="relative inline-flex items-center gap-3 px-10 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold text-base shadow-xl shadow-violet-600/30 hover:shadow-violet-600/50 hover:scale-[1.02] transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {/* Subtle pulse ring */}
              {!generating && (
                <span className="absolute inset-0 rounded-2xl bg-violet-500/40 animate-ping pointer-events-none" aria-hidden="true" />
              )}
              <Sparkles className={`w-5 h-5 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Analysing your firm - 30-60 seconds…' : 'Generate Snapshot'}
              {!generating && <Loader2 className="w-4 h-4 opacity-0" />}
            </button>
            <p className="text-xs text-slate-400 mt-3">Free for your firm - generates once. Admin team can refresh anytime.</p>
            {error && (
              <div className="mt-6 px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700 max-w-md text-center" data-testid="generate-snapshot-error">
                {error}
              </div>
            )}
          </div>

          <div className="mt-16 grid sm:grid-cols-3 gap-4">
            {[
              { icon: Search, title: 'Keyword Opportunities', body: '14-18 law-firm-specific search opportunities with AI-estimated demand and revenue ranges.' },
              { icon: Bot, title: 'AI Visibility Readiness', body: 'How discoverable your firm is in ChatGPT, Gemini, and Perplexity-style answers.' },
              { icon: Lightbulb, title: 'Recommended Actions', body: 'Practical next steps to lift your search and AI engine visibility.' },
            ].map((b) => (
              <div key={b.title} className="p-5 rounded-2xl border border-slate-200 bg-slate-50/50">
                <b.icon className="w-5 h-5 text-violet-600" />
                <h3 className="mt-3 text-sm font-bold text-slate-900">{b.title}</h3>
                <p className="mt-1 text-xs text-slate-600 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── After-generation state ─────────────────────────────────────
  const avr = snapshot.ai_visibility_readiness || {};
  const subScores = avr.sub_scores || {};
  const generatedAt = snapshot.generated_at ? new Date(snapshot.generated_at).toLocaleString() : '-';
  const currencySymbol = snapshot.currency_symbol || '€';

  return (
    <div className="bg-white min-h-[80vh] -m-6 p-6 sm:p-10" data-testid="growth-snapshot-loaded">
      <div className="max-w-6xl mx-auto space-y-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-50 border border-violet-100 text-violet-700 text-[11px] font-bold uppercase tracking-wider">
              <Sparkles className="w-3 h-3" /> AI Estimated
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Growth Opportunity Snapshot
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Generated: <span className="font-medium text-slate-700">{generatedAt}</span>
              {snapshot.country ? <> · Country: <span className="font-medium text-slate-700">{snapshot.country}</span></> : null}
              {snapshot.currency_code ? <> · Revenue currency: <span className="font-medium text-slate-700">{snapshot.currency_code}</span></> : null}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              disabled={sharing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold shadow hover:bg-violet-700 transition disabled:opacity-50"
              data-testid="share-snapshot-btn"
            >
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
              Share link
            </button>
            {canEdit && (
              <button
                onClick={() => handleGenerate(true)}
                disabled={generating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold shadow hover:bg-slate-800 transition disabled:opacity-50"
                data-testid="regenerate-snapshot-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {generating ? 'Regenerating…' : 'Regenerate'}
              </button>
            )}
          </div>
        </div>

        {/* Public share link panel */}
        {shareUrl && (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="share-link-panel">
            <div className="flex items-center gap-2 text-sm text-violet-800 font-semibold">
              <Share2 className="w-4 h-4" /> Public view-only link
            </div>
            <input
              readOnly
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-violet-200 bg-white text-sm text-slate-700"
              data-testid="share-link-input"
            />
            <button onClick={copyShare} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition shrink-0" data-testid="share-link-copy">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        )}

        {error && (
          <div className="px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-sm text-rose-700">{error}</div>
        )}

        {/* AI Visibility Readiness card */}
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-6 sm:p-8 shadow-sm" data-testid="ai-visibility-card">
          <div className="flex flex-col lg:flex-row gap-8 items-start lg:items-center">
            <div className="flex flex-col items-center gap-3 shrink-0">
              <ScoreRing score={avr.overall_score} label="Overall" />
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">AI Visibility</div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Bot className="w-5 h-5 text-violet-600" /> AI Visibility Readiness
              </h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{avr.summary || '-'}</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {Object.entries(SUBSCORE_LABEL).map(([k, lbl]) => {
                  const v = Number(subScores[k] || 0);
                  return (
                    <div key={k} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-slate-100">
                      <span className="text-xs font-medium text-slate-700">{lbl}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.max(0, Math.min(100, v))}%`,
                              background: v < 45 ? '#dc2626' : v < 65 ? '#f59e0b' : '#10b981',
                            }}
                          />
                        </div>
                        <span className="text-xs font-bold text-slate-900 w-8 text-right">{v}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Top 3 opportunity cards */}
        {top3.length > 0 && (
          <div data-testid="top-opportunities">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
              <Crown className="w-5 h-5 text-amber-500" /> Top 3 Opportunities
            </h2>
            <div className="grid md:grid-cols-3 gap-4">
              {top3.map((o, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">#{i + 1}</div>
                    <div className="px-2 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-bold border border-violet-100">Score {o.opportunity_score}</div>
                  </div>
                  <h3 className="mt-2 text-base font-bold text-slate-900 leading-snug">{o.keyword}</h3>
                  <div className="mt-2 flex items-center gap-2"><IntentBadge intent={o.intent} /></div>
                  <div className="mt-4 space-y-1.5 text-xs">
                    <div className="flex items-center justify-between"><span className="text-slate-500">Searches/mo</span><span className="font-semibold text-slate-900">{o.estimated_monthly_searches_range || '-'}</span></div>
                    <div className="flex items-center justify-between"><span className="text-slate-500">Revenue/mo</span><span className="font-semibold text-emerald-700">{o.estimated_monthly_revenue_range || '-'}</span></div>
                  </div>
                  <p className="mt-3 text-xs text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{o.recommended_action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full keyword opportunities table */}
        <div data-testid="keyword-opportunities-table">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
            <TrendingUp className="w-5 h-5 text-violet-600" /> Keyword Opportunities
            <span className="ml-2 text-xs font-normal text-slate-500">({snapshot.keyword_opportunities?.length || 0} AI-estimated)</span>
          </h2>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Keyword</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Intent</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Searches/mo</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Revenue/mo ({currencySymbol})</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Score</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Recommended action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {(snapshot.keyword_opportunities || []).map((o, i) => (
                  <tr key={i} className="hover:bg-slate-50/60 transition">
                    <td className="px-4 py-3 font-medium text-slate-900">{o.keyword}</td>
                    <td className="px-4 py-3"><IntentBadge intent={o.intent} /></td>
                    <td className="px-4 py-3 text-right text-slate-700">{o.estimated_monthly_searches_range || '-'}</td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{o.estimated_monthly_revenue_range || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-violet-50 text-violet-700 text-xs font-bold border border-violet-100">
                        {o.opportunity_score ?? '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs leading-relaxed max-w-md">{o.recommended_action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Opportunities Detected */}
        {snapshot.opportunities_detected?.length > 0 && (
          <div data-testid="opportunities-detected">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
              <BadgeAlert className="w-5 h-5 text-amber-500" /> Opportunities Detected
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {snapshot.opportunities_detected.map((op, i) => {
                const sev = (op.severity || 'medium').toLowerCase();
                const sevCls = sev === 'high' ? 'bg-rose-50 border-rose-200 text-rose-700'
                  : sev === 'low' ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-amber-50 border-amber-200 text-amber-700';
                return (
                  <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="text-sm font-bold text-slate-900 leading-snug">{op.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${sevCls}`}>{sev}</span>
                        </div>
                        <p className="mt-2 text-xs text-slate-600 leading-relaxed">{op.detail}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Recommended actions */}
        {snapshot.recommended_actions?.length > 0 && (
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-violet-50/30 p-6 sm:p-8" data-testid="recommended-actions">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit' }}>
              <Target className="w-5 h-5 text-violet-600" /> Recommended Actions
            </h2>
            <ul className="space-y-3">
              {snapshot.recommended_actions.map((a, i) => (
                <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white border border-slate-100">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <span className="text-sm text-slate-700 leading-relaxed">{a}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Disclaimer */}
        <div className="px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
          <strong className="font-semibold text-slate-800">Disclaimer:</strong> These insights are AI-estimated opportunity indicators and not real-time rankings or live AI engine visibility data.
        </div>

        {/* CTA: Unlock SEO + AEO */}
        {isClient && (
          <div className="rounded-3xl bg-gradient-to-br from-violet-600 via-indigo-600 to-violet-700 p-8 sm:p-10 shadow-2xl shadow-violet-500/30 text-center" data-testid="snapshot-upgrade-cta">
            <Sparkles className="w-7 h-7 text-white mx-auto" />
            <h2 className="mt-4 text-3xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Unlock SEO + AEO</h2>
            <p className="mt-3 text-sm text-violet-100 max-w-2xl mx-auto leading-relaxed">
              Unlock live AI visibility intelligence, prompt tracking, competitor AI comparison, and implementation support with a paid SEO + AEO plan.
            </p>
            <Link
              to="/plan-selection"
              className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-white text-violet-700 font-bold text-sm shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all"
              data-testid="snapshot-unlock-cta-btn"
            >
              Unlock SEO + AEO
              <TrendingUp className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>

      <ScrollIndicator visible={showScrollHint} />
    </div>
  );
}
